import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/outline";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { fetchClinicInvoices } from "../store/thunks/reportsThunk";

const formatCurrency = (amount) => {
  if (amount == null) return "$0.00";
  return `$${Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

const RevenueClinicInvoicesModal = ({ isOpen, onClose, clinic, startDate, endDate }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    const loadInvoices = async () => {
      if (!isOpen || !clinic?.id) return;
      setLoading(true);
      setError(null);
      try {
        const action = await dispatch(
          fetchClinicInvoices({ startDate, endDate, clinicId: clinic.id })
        );
        const payload = action.payload;
        const fetched = payload?.data?.invoices || [];
        setInvoices(Array.isArray(fetched) ? fetched : []);
      } catch (err) {
        setError("Failed to load clinic invoices");
      } finally {
        setLoading(false);
      }
    };
    loadInvoices();
  }, [isOpen, clinic?.id, startDate, endDate, dispatch]);

  const totals = useMemo(() => {
    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    return { totalAmount, count: invoices.length };
  }, [invoices]);

  const handleDownloadExcel = () => {
    if (!invoices.length) return;
    const rows = invoices.map((inv) => ({
      "Invoice ID": inv.invoiceId || inv._id,
      "Service Date": inv.serviceDate ? format(new Date(inv.serviceDate), "yyyy-MM-dd") : "",
      "Client": inv.client ? `${inv.client.firstName || ''} ${inv.client.lastName || ''}`.trim() : "",
      "Clinician": inv.clinician ? `${inv.clinician.firstName || ''} ${inv.clinician.lastName || ''}`.trim() : "",
      "Service": Array.isArray(inv.services) && inv.services.length
        ? inv.services.map(s => s?.description).filter(Boolean).join(", ")
        : "",
      "Status": inv.status || "",
      "Amount": Number(inv.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
    const safeClinic = (clinic?.name || "Clinic").replace(/[^a-z0-9_\- ]/gi, "").replace(/\s+/g, "_");
    XLSX.writeFile(workbook, `Clinic_Invoices_${safeClinic}_${startDate}_to_${endDate}.xlsx`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-sm shadow-xl w-[95vw] max-w-5xl h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{clinic?.name || "Clinic"} Invoices</h3>
            <p className="text-xs text-gray-500">{startDate} to {endDate}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadExcel}
              disabled={!invoices.length}
              className="inline-flex items-center justify-center rounded-sm text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/20 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-indigo-600 text-white hover:bg-indigo-500 h-8 px-3 py-1.5 shadow-sm"
            >
              Download Excel
            </button>
            <button
              type="button"
              className="rounded-sm bg-white text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 h-8 w-8 flex items-center justify-center"
              onClick={onClose}
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {error && (
            <div className="text-red-600 text-sm mb-3">{error}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
              <div className="text-xs text-gray-500">Invoices</div>
              <div className="text-lg font-semibold text-gray-900">{totals.count}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
              <div className="text-xs text-gray-500">Total Amount</div>
              <div className="text-lg font-semibold text-gray-900">{formatCurrency(totals.totalAmount)}</div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-6 pb-6">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-gray-600 text-sm">Loading invoices...</div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-gray-600 text-sm">No invoices found for this clinic in the selected date range.</div>
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-sm">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Invoice ID</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Service Date</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Client</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Clinician</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {invoices.map((inv) => (
                    <tr key={inv._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{inv.invoiceId || inv._id}</td>
                      <td className="px-3 py-2">{inv.serviceDate ? format(new Date(inv.serviceDate), "MMM dd, yyyy") : ""}</td>
                      <td className="px-3 py-2">{inv.client ? `${inv.client.firstName || ''} ${inv.client.lastName || ''}`.trim() : ""}</td>
                      <td className="px-3 py-2">{inv.clinician ? `${inv.clinician.firstName || ''} ${inv.clinician.lastName || ''}`.trim() : ""}</td>
                      <td className="px-3 py-2">{Array.isArray(inv.services) && inv.services.length
                        ? inv.services.map(s => s?.description).filter(Boolean).join(", ")
                        : ""}
                      </td>
                      <td className="px-3 py-2">{inv.status || ""}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(inv.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueClinicInvoicesModal;


